import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';
import { fetchProfile, updateProfile } from '../services/api';
import { supabase } from '../services/supabase';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast, showError, showSuccess, dismiss } = useToast();

  const [calendarSync, setCalendarSync] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    try {
      const profile = await fetchProfile();
      setCalendarSync(profile.google_calendar_sync);
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleCalendarSync = async () => {
    const newValue = !calendarSync;
    setToggling(true);

    try {
      // If enabling and we need refresh token, store it
      if (newValue) {
        const { data: { session } } = await supabase.auth.getSession();
        const refreshToken = session?.provider_refresh_token ?? null;
        await updateProfile({
          google_calendar_sync: true,
          google_refresh_token: refreshToken,
        });
      } else {
        await updateProfile({ google_calendar_sync: false });
      }

      setCalendarSync(newValue);
      showSuccess(newValue ? 'Calendar sync enabled' : 'Calendar sync disabled');
    } catch {
      showError('Failed to update settings');
    }
    setToggling(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/')} type="button">
          &larr; Back
        </button>
        <span style={styles.title}>Settings</span>
      </div>

      {/* User info */}
      {user && (
        <div style={styles.section}>
          <p style={styles.email}>{user.email}</p>
        </div>
      )}

      {/* Calendar sync */}
      <div style={styles.section}>
        <label style={styles.label}>Google Calendar</label>
        <div style={styles.toggleRow}>
          <span style={styles.toggleLabel}>Sync reminders to Google Calendar</span>
          <button
            type="button"
            style={{
              ...styles.toggleBtn,
              backgroundColor: calendarSync ? 'var(--color-success)' : 'var(--color-surface)',
            }}
            onClick={handleToggleCalendarSync}
            disabled={loading || toggling}
          >
            <span style={{
              ...styles.toggleKnob,
              transform: calendarSync ? 'translateX(18px)' : 'translateX(0)',
            }} />
          </button>
        </div>
        <p style={styles.hint}>
          {calendarSync
            ? 'High-confidence reminders will automatically sync to your Google Calendar.'
            : 'Enable to automatically create Google Calendar events from voice note reminders.'
          }
        </p>
      </div>

      {/* Sign out */}
      <div style={styles.section}>
        <button
          style={styles.signOutBtn}
          onClick={signOut}
          type="button"
        >
          Sign out
        </button>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismiss} />}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    padding: 'var(--space-4)',
    gap: 'var(--space-6)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  backBtn: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-accent)',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  title: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  email: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  label: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-3)',
  },
  toggleLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    flex: 1,
  },
  toggleBtn: {
    position: 'relative' as const,
    width: '42px',
    height: '24px',
    borderRadius: '12px',
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-fast)',
    flexShrink: 0,
    padding: 0,
  },
  toggleKnob: {
    position: 'absolute' as const,
    top: '2px',
    left: '2px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'transform var(--transition-fast)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  hint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
  },
  signOutBtn: {
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-danger)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    textAlign: 'center' as const,
  },
};
